/**
 * HTTP 客户端
 * 自动处理 RSA/AES 加密
 */

import type { ApiResponse, EncryptedResponse } from '@/types/api'
import { encryptRequest, decryptResponse, generateUUID, getTimestamp, isWebCryptoAvailable } from './crypto'

// API 基础 URL
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

// 敏感字段名匹配 (打印日志前脱敏,避免密码/token 明文出现在控制台)
const SENSITIVE_KEY_PATTERN = /password|pwd|secret|token/i

function maskSensitive(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(maskSensitive)
  if (value && typeof value === 'object') {
    const masked: Record<string, unknown> = {}
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      masked[key] = SENSITIVE_KEY_PATTERN.test(key) ? '***' : maskSensitive(val)
    }
    return masked
  }
  return value
}

// Token 存储 key
const ACCESS_TOKEN_KEY = 'access_token'
const REFRESH_TOKEN_KEY = 'refresh_token'
const TOKEN_EXPIRES_KEY = 'token_expires'

// 触发强制登出的鉴权失效码 (对应 ApiErrorCode):
//   100005 TOKEN_EXPIRED  (含"账号已在其他设备登录"被踢下线)
//   100006 TOKEN_INVALID
//   100010 旧版 token 过期码 (保留兼容)
const AUTH_FAILURE_CODES = new Set([100005, 100006, 100010])

// 需要先弹确认框、用户点确认后再跳登录的码 (账号在其他设备登录)
const KICKED_OUT_CODE = 100005

function isAuthFailure(code: number): boolean {
  return AUTH_FAILURE_CODES.has(code)
}

// 防止多个并发请求同时返回失效码时反复触发登出/跳转/弹框
let authFailureHandled = false

/**
 * 处理登录失效：清除 token 并跳登录页
 *
 * - 100005 (账号在其他设备登录): 先弹确认框, 用户点确认后再跳
 * - 其它失效码: 直接静默跳登录页
 */
function handleTokenExpired(code: number, msg?: string): void {
  if (authFailureHandled) return
  authFailureHandled = true
  clearTokens()

  if (code === KICKED_OUT_CODE) {
    // 账号在其他设备登录: 走全局 KickedOutDialog (金色主题, 与项目其它弹窗一致),
    // 用户点确认后再跳登录
    import('@/composables/useKickedOut')
      .then(({ showKickedOut }) => showKickedOut(msg))
      .catch(() => {
        // 弹窗模块加载失败 → 兜底直接跳
      })
      .then(() => redirectToLogin())
    return
  }

  redirectToLogin()
}

/**
 * 用 Vue Router 跳登录页
 * - 动态 import('@/router') 取路由实例 (路由守卫同款写法, 避免循环依赖)
 * - 带 redirect 回跳; 已在登录页则不跳
 * - 跳转结束复位 authFailureHandled, 以便下次会话再次生效
 */
function redirectToLogin(): void {
  import('@/router')
    .then(({ default: router }) => {
      const current = router.currentRoute.value
      if (String(current.name ?? '').includes('login')) return
      return router.replace({
        name: 'login',
        query: { redirect: current.fullPath },
      })
    })
    .catch(() => {
      // 导航被取消/重复等非致命错误忽略
    })
    .finally(() => {
      authFailureHandled = false
    })
}

/**
 * 获取设备指纹
 * 简单实现，实际项目中可以使用 fingerprintjs 等库
 *
 * 导出给非 post()/get() 走的请求复用 (如视讯登录凭据 SSE, 见 services/sse/liveToken.ts):
 * 那类端点跟这里的加密接口一样过 AuthMiddleware, 必须带一模一样的指纹值,
 * 不然后端会认成"指纹不匹配"而不是"缺失", 现象都是 code=100006。
 */
export function getDeviceFingerprint(): string {
  const stored = localStorage.getItem('device_fingerprint')
  if (stored) {
    return stored
  }

  const fingerprint = generateUUID()
  localStorage.setItem('device_fingerprint', fingerprint)
  return fingerprint
}

/**
 * 获取存储的 Access Token
 */
export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY)
}

/**
 * 获取存储的 Refresh Token
 */
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY)
}

/**
 * 保存认证 Token
 */
export function saveTokens(accessToken: string, refreshToken: string, expiresAt: number): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  localStorage.setItem(TOKEN_EXPIRES_KEY, expiresAt.toString())
}

/**
 * 清除认证 Token
 */
export function clearTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem(TOKEN_EXPIRES_KEY)
}

/**
 * 检查 Token 是否过期
 */
export function isTokenExpired(): boolean {
  const expiresAt = localStorage.getItem(TOKEN_EXPIRES_KEY)
  if (!expiresAt) return true

  const now = Math.floor(Date.now() / 1000)
  // 提前 5 分钟认为过期
  return now >= parseInt(expiresAt) - 300
}

/**
 * HTTP 请求配置
 */
interface RequestConfig {
  /** 是否需要加密（默认 true） */
  encrypt?: boolean
  /** 是否需要认证（默认 false） */
  auth?: boolean
  /** 超时时间（毫秒） */
  timeout?: number
  /**
   * 页面卸载 (刷新/关闭标签页) 时的尽力而为请求用: 让请求脱离文档生命周期,
   * 页面已经开始卸载也能继续把请求发出去 (旧的 unload 期间发起的普通 fetch
   * 会被直接中断)。仅在 pagehide 场景传 true, 平时不需要。
   */
  keepalive?: boolean
}

/**
 * 发送 POST 请求（加密）
 */
export async function post<TReq extends object, TRes>(
  url: string,
  data: TReq,
  config: RequestConfig = {}
): Promise<ApiResponse<TRes>> {
  const { encrypt: requestEncrypt = true, auth = false, timeout = 30000, keepalive = false } = config

  // 检测 Web Crypto API 是否可用；不可用时拒绝请求而非降级明文发送 (fail-closed)
  const encrypt = requestEncrypt
  if (encrypt && !isWebCryptoAvailable()) {
    throw new Error('Web Crypto API 不可用（需要 HTTPS 安全上下文），已拒绝请求以避免明文传输')
  }

  // 添加安全字段
  const requestData = {
    ...data,
    nonce: generateUUID(),
    timestamp: getTimestamp(),
  }

  // 构建请求头
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Device-Fingerprint': getDeviceFingerprint(),
  }

  // 添加认证头
  if (auth) {
    const token = getAccessToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
  }

  let body: string
  let aesKey: CryptoKey | undefined

  // 打印加密前的请求数据（heartbeat 不打印，敏感字段已脱敏）
  if (!url.includes('heartbeat')) {
    console.log(`[HTTP] 请求 ${url} 加密前:`, maskSensitive(requestData))
  }

  if (encrypt) {
    const encrypted = await encryptRequest(requestData)
    aesKey = encrypted.aesKey

    body = JSON.stringify({
      data: {
        encrypted_key: encrypted.encryptedKey,
        encrypted_data: encrypted.encryptedData,
        iv: encrypted.iv,
      },
    })
  } else {
    body = JSON.stringify(requestData)
  }

  // 创建 AbortController 用于超时
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeout)

  try {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'POST',
      headers,
      body,
      signal: controller.signal,
      keepalive,
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      throw new Error(`HTTP Error: ${response.status}`)
    }

    const result = await response.json()

    // 检查登录失效
    if (isAuthFailure(result.code)) {
      handleTokenExpired(result.code, result.msg)
      return result as ApiResponse<TRes>
    }

    // 检查外层响应状态（未加密的错误直接返回）
    if (result.code !== 0 && !result.data?.encrypted_data) {
      return result as ApiResponse<TRes>
    }

    // 如果响应是加密的，解密它
    if (encrypt && aesKey && result.data?.encrypted_data) {
      const encryptedRes = result as ApiResponse<EncryptedResponse['data']>
      const decryptedData = await decryptResponse<{ code: number; msg: string; data: TRes }>(
        encryptedRes.data.encrypted_data,
        encryptedRes.data.iv,
        aesKey
      )
      // 打印解密后的响应数据（heartbeat 不打印，敏感字段已脱敏）
      if (!url.includes('heartbeat')) {
        console.log(`[HTTP] 响应 ${url} 解密后:`, maskSensitive(decryptedData))
      }

      // 检查解密后的登录失效
      if (isAuthFailure(decryptedData.code)) {
        handleTokenExpired(decryptedData.code, decryptedData.msg)
      }

      return {
        code: decryptedData.code ?? 0,
        msg: decryptedData.msg ?? 'success',
        data: decryptedData.data,
      }
    }

    // 打印未加密的响应数据（heartbeat 不打印，敏感字段已脱敏）
    if (!url.includes('heartbeat')) {
      console.log(`[HTTP] 响应 ${url}:`, maskSensitive(result))
    }
    return result as ApiResponse<TRes>
  } catch (error) {
    clearTimeout(timeoutId)

    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timeout')
    }

    throw error
  }
}

/**
 * 发送 GET 请求（不加密）
 */
export async function get<TRes>(
  url: string,
  params?: Record<string, string>,
  config: RequestConfig = {}
): Promise<ApiResponse<TRes>> {
  const { auth = false, timeout = 30000 } = config

  // 构建 URL
  const searchParams = new URLSearchParams(params)
  const fullUrl = params ? `${API_BASE_URL}${url}?${searchParams.toString()}` : `${API_BASE_URL}${url}`

  // 构建请求头
  const headers: Record<string, string> = {
    'X-Device-Fingerprint': getDeviceFingerprint(),
  }

  if (auth) {
    const token = getAccessToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
  }

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeout)

  try {
    const response = await fetch(fullUrl, {
      method: 'GET',
      headers,
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      throw new Error(`HTTP Error: ${response.status}`)
    }

    const result = await response.json()

    // 检查登录失效
    if (isAuthFailure(result.code)) {
      handleTokenExpired(result.code, result.msg)
    }

    return result
  } catch (error) {
    clearTimeout(timeoutId)

    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timeout')
    }

    throw error
  }
}
